import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { AlertTriangle, Code, Copy, History, LucideBot, LucideFileText } from "lucide-react"
import { type AIMonitorConfig, type PromptServiceConfig } from "@/types/config"
import { LLMServiceType, LLM_COSTS, LLM_DISPLAY_NAMES } from "@/types/llm"
import { useState } from "react"

interface PromptSettingsProps {
  promptText?: string
  onUpdate: (config: Partial<AIMonitorConfig>) => void
  isLoading?: boolean
  // New props for bill review, opposition, and support prompts
  billReviewPrompt?: PromptServiceConfig
  oppositionPrompt?: PromptServiceConfig
  supportPrompt?: PromptServiceConfig
  onPromptUpdate?: (key: "LegislativeReview" | "Opposition" | "Support" | "ShouldMonitor", updates: Partial<PromptServiceConfig>) => void
}

const DEFAULT_PROMPT = `### Prompt Focus
Evaluate legislative impact on:
1. UMD College Park (UMCP)
2. Higher education sector

### Rating Scale
- Impact scores: 1-10 (minimal to extreme)
- Impact types: positive/negative/neutral
- Includes rationale for scores`

export function PromptSettings({ promptText, onUpdate, isLoading, billReviewPrompt, oppositionPrompt, supportPrompt, onPromptUpdate }: PromptSettingsProps) {
  const [showWarning, setShowWarning] = useState(false)

  const handlePromptChange = (value: string) => {
    if (!showWarning) setShowWarning(true)
    onUpdate({ PromptText: value })
  }

  const resetPrompt = () => {
    onUpdate({ PromptText: DEFAULT_PROMPT })
    setShowWarning(false)
  }

  const formatCost = (llmType: LLMServiceType): string => {
    const cost = LLM_COSTS[llmType]
    return `$${cost.inputCost}/1K input, $${cost.outputCost}/1K output`
  }

  const renderPromptConfig = (key: "LegislativeReview" | "Opposition" | "Support" | "ShouldMonitor", value: PromptServiceConfig | undefined) => {
    if (!value || !onPromptUpdate) return null
    return (
      <div className="flex flex-col md:flex-row gap-4 mt-6">
        <div className="w-full md:w-64 flex-none space-y-2">
          <div className="flex items-center gap-2">
            <LucideBot className="h-4 w-4" />
            <Label>AI Model</Label>
          </div>
          <Select
            value={value.ServiceType}
            onValueChange={(val) => onPromptUpdate(key, { ServiceType: val as LLMServiceType })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select AI model" />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(LLM_COSTS) as LLMServiceType[]).map((llmType) => (
                <SelectItem key={llmType} value={llmType}>
                  <div className="flex flex-col">
                    <span>{LLM_DISPLAY_NAMES[llmType]}</span>
                    <span className="text-xs text-muted-foreground">{formatCost(llmType)}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-grow space-y-2">
          <div className="flex items-center gap-2">
            <LucideFileText className="h-4 w-4" />
            <Label htmlFor={`${key}-prompt-textarea`} id={`${key}-prompt-label`}>Prompt</Label>
          </div>
          <textarea
            id={`${key}-prompt-textarea`}
            aria-labelledby={`${key}-prompt-label`}
            value={value.Text ?? ''}
            onChange={(e) => onPromptUpdate(key, { Text: e.target.value })}
            placeholder={`Enter ${key.match(/[A-Z][a-z]+/g)?.join(' ')} prompt...`}
            className="min-h-[120px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Monitor Prompt Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5 text-blue-500" />
            Monitoring Prompt
          </CardTitle>
          <CardDescription>
            System prompt for AI analysis
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {showWarning && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>
                Changes to the prompt may affect response parsing and analysis quality
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-sm font-medium">Analysis Prompt</label>
              <div className="space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigator.clipboard.writeText(promptText || DEFAULT_PROMPT)}
                  disabled={isLoading}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetPrompt}
                  disabled={isLoading}
                >
                  <History className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              </div>
            </div>
            <Textarea
              value={promptText || DEFAULT_PROMPT}
              onChange={(e) => handlePromptChange(e.target.value)}
              rows={10}
              disabled={isLoading}
              className="font-mono text-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Bill Review Prompts Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="h-5 w-5 text-blue-500" />
            Bill Review Prompts
          </CardTitle>
          <CardDescription>
            Configure prompts for bill analysis and testimony generation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium">Bill Review Prompt</Label>
              {renderPromptConfig('LegislativeReview', billReviewPrompt)}
            </div>
            <div>
              <Label className="text-base font-medium">Opposition Testimony Prompt</Label>
              {renderPromptConfig('Opposition', oppositionPrompt)}
            </div>
            <div>
              <Label className="text-base font-medium">Support Testimony Prompt</Label>
              {renderPromptConfig('Support', supportPrompt)}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}